import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
  ReportSummary,
  SearchScope,
  useDeleteReport,
  usePatchReport,
  usePinReport,
  useReports,
} from '../api';

export default function Reports() {
  const [scopeFilter, setScopeFilter] = useState<'all' | SearchScope>('all');
  const { data, isLoading, error } = useReports(
    scopeFilter === 'all' ? {} : { scope: scopeFilter },
  );

  if (isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (error) return <p className="text-rose-600 dark:text-rose-400">Failed to load reports.</p>;
  const reports = data?.reports ?? [];
  const pinned = reports.filter((r) => r.is_pinned);
  const others = reports.filter((r) => !r.is_pinned);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-base font-semibold">Saved reports</h1>
        <div className="flex items-center gap-2 text-xs">
          <ScopeFilter value={scopeFilter} onChange={setScopeFilter} />
          <Link
            to="/search"
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            + New search
          </Link>
        </div>
      </header>

      {reports.length === 0 && (
        <p className="rounded border border-dashed border-slate-200 dark:border-slate-700 px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
          No saved reports yet. Build a filter on the <Link to="/search" className="text-slate-700 dark:text-slate-300 underline">Search page</Link>{' '}
          and click "Save as report…".
        </p>
      )}

      {pinned.length > 0 && (
        <Section title="Pinned">
          <ReportList reports={pinned} />
        </Section>
      )}
      {others.length > 0 && (
        <Section title={pinned.length > 0 ? 'All' : 'Reports'}>
          <ReportList reports={others} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h2>
      {children}
    </section>
  );
}

function ScopeFilter({
  value,
  onChange,
}: {
  value: 'all' | SearchScope;
  onChange: (v: 'all' | SearchScope) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded border border-slate-300 dark:border-slate-600">
      {(['all', 'people', 'households'] as const).map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          className={`px-2 py-0.5 capitalize ${
            value === s ? 'bg-slate-900 dark:bg-slate-700 text-white' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function ReportList({ reports }: { reports: ReportSummary[] }) {
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      {reports.map((r) => (
        <ReportRow key={r.id} r={r} />
      ))}
    </ul>
  );
}

function ReportRow({ r }: { r: ReportSummary }) {
  const pin = usePinReport();
  const del = useDeleteReport();
  const patch = usePatchReport();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(r.name);
  const [description, setDescription] = useState(r.description ?? '');

  const togglePin = () => pin.mutate({ id: r.id, pin: !r.is_pinned });
  const remove = () => {
    if (!confirm(`Delete report "${r.name}"? This can't be undone.`)) return;
    del.mutate(r.id);
  };
  const saveName = () => {
    patch.mutate(
      { id: r.id, body: { name: name.trim() || r.name, description: description.trim() || null } },
      { onSuccess: () => setRenaming(false) },
    );
  };

  return (
    <li className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
      <button
        onClick={togglePin}
        className={`text-base ${r.is_pinned ? 'text-amber-500 dark:text-amber-400' : 'text-slate-300 dark:text-slate-600 hover:text-amber-400'}`}
        title={r.is_pinned ? 'Unpin' : 'Pin to top'}
        aria-label={r.is_pinned ? 'Unpin' : 'Pin'}
      >
        ★
      </button>
      <div className="min-w-0 flex-1">
        {renaming ? (
          <div className="flex flex-wrap items-center gap-1">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-sm"
            />
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              className="w-64 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
            />
            <button
              onClick={saveName}
              disabled={patch.isPending}
              className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => {
                setRenaming(false);
                setName(r.name);
                setDescription(r.description ?? '');
              }}
              className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <Link
              to={`/search?report=${r.id}`}
              className="font-medium text-slate-800 dark:text-slate-200 hover:underline"
            >
              {r.name}
            </Link>
            {r.description && (
              <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">{r.description}</span>
            )}
          </>
        )}
        <div className="text-xs text-slate-400 dark:text-slate-500">
          {r.scope} · created by {r.created_by} ·{' '}
          updated {new Date(r.updated_at).toLocaleDateString()}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Link
          to={`/search?report=${r.id}`}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          title="Open in the search builder"
        >
          Open
        </Link>
        <Link
          to={`/maps?report=${r.id}`}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          title="Draw these on the ward map"
        >
          Map
        </Link>
        {!renaming && (
          <button
            onClick={() => setRenaming(true)}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Rename
          </button>
        )}
        <button
          onClick={remove}
          disabled={del.isPending}
          className="rounded border border-rose-200 dark:border-rose-900 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-50"
        >
          Delete
        </button>
      </div>
    </li>
  );
}
